import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  Chip,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  Slider,
} from "@mui/material";
import {
  Navigation as NavigationIcon,
  SwapVert as SwapIcon,
  Clear as ClearIcon,
  Schedule as TimeIcon,
  Straighten as DistanceIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
} from "@mui/icons-material";
import MapView from "./MapView";
import { config } from "../config";

function RoutePlanner() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [startAddress, setStartAddress] = useState("");
  const [endAddress, setEndAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState(null);
  const [error, setError] = useState("");
  const [startSuggestions, setStartSuggestions] = useState([]);
  const [endSuggestions, setEndSuggestions] = useState([]);
  const [startSuggestionsCooldown, setStartSuggestionsCooldown] =
    useState(false);
  const [endSuggestionsCooldown, setEndSuggestionsCooldown] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [currentInstructionIndex, setCurrentInstructionIndex] = useState(0);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [lastSpokenInstruction, setLastSpokenInstruction] = useState(-1);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [selectedRouteType, setSelectedRouteType] = useState(null);

  // Text-to-Speech für Anweisungen
  const speakInstruction = (text) => {
    if (!voiceEnabled || !("speechSynthesis" in window)) return;

    // Stoppe vorherige Ansage
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 0.8;

    window.speechSynthesis.speak(utterance);
  };

  // Adress-Autocomplete Funktion mit verbesserter Formatierung
  const searchAddresses = async (query) => {
    if (query.length < 2) {
      return [];
    }

    try {
      // Verbesserte Suche: Auch bei längeren/bereits ausgewählten Adressen
      const searchQuery = query.trim();

      // Verwende nur echte API-Calls (Mock-Daten entfernt)

      const url =
        config.getApiUrl("/geocoding") +
        `?q=${encodeURIComponent(searchQuery)}`;
      console.log("Geocoding URL:", url);

      const response = await fetch(url);
      const data = await response.json();

      console.log("Geocoding Response:", data);

      if (data.success && data.results) {
        console.log("Raw results:", data.results);

        // Formatiere Ergebnisse für bessere Darstellung
        const formattedResults = data.results.slice(0, 8).map((result) => ({
          ...result,
          formatted_address: formatAddress(result),
        }));

        console.log("Formatted results:", formattedResults);

        // Sortiere nach Relevanz: Exakte Matches zuerst
        const sortedResults = formattedResults.sort((a, b) => {
          const aFormatted = (
            a.formatted_address || a.display_name
          ).toLowerCase();
          const bFormatted = (
            b.formatted_address || b.display_name
          ).toLowerCase();
          const queryLower = searchQuery.toLowerCase();

          const aStartsWith = aFormatted.startsWith(queryLower);
          const bStartsWith = bFormatted.startsWith(queryLower);

          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;

          return 0;
        });

        console.log("Final sorted results:", sortedResults);
        return sortedResults;
      } else {
        console.log("No results or API error:", data);
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
    return [];
  };

  // Adresse ohne Kommas formatieren: "Land Ort Straße Hausnummer"
  const formatAddress = (addressData) => {
    if (!addressData.address) return addressData.display_name;

    const parts = [];
    const addr = addressData.address;

    // Land (optional, nur wenn nicht Deutschland)
    if (
      addr.country &&
      addr.country !== "Deutschland" &&
      addr.country !== "Germany"
    ) {
      parts.push(addr.country);
    }

    // Ort/Stadt
    if (addr.city || addr.town || addr.village) {
      parts.push(addr.city || addr.town || addr.village);
    }

    // Straße und Hausnummer
    if (addr.road) {
      const streetPart = addr.house_number
        ? `${addr.road} ${addr.house_number}`
        : addr.road;
      parts.push(streetPart);
    }

    return parts.join(" ") || addressData.display_name;
  };

  // Debounced address search für Start
  useEffect(() => {
    if (startSuggestionsCooldown) return; // Skip während Cooldown

    const timeoutId = setTimeout(async () => {
      const trimmedAddress = startAddress.trim();
      if (trimmedAddress.length >= 2) {
        console.log("Searching for start address:", trimmedAddress);
        const suggestions = await searchAddresses(trimmedAddress);
        console.log("Start suggestions received:", suggestions);

        // Filtere identische Ergebnisse
        const uniqueSuggestions = suggestions.filter((suggestion) => {
          const suggestionText =
            suggestion.formatted_address || suggestion.display_name;
          return suggestionText.toLowerCase() !== trimmedAddress.toLowerCase();
        });

        setStartSuggestions(uniqueSuggestions);
      } else {
        setStartSuggestions([]);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [startAddress, startSuggestionsCooldown]);

  // Debounced address search für Ziel
  useEffect(() => {
    if (endSuggestionsCooldown) return; // Skip während Cooldown

    const timeoutId = setTimeout(async () => {
      const trimmedAddress = endAddress.trim();
      if (trimmedAddress.length >= 2) {
        console.log("Searching for end address:", trimmedAddress);
        const suggestions = await searchAddresses(trimmedAddress);
        console.log("End suggestions received:", suggestions);

        // Filtere identische Ergebnisse
        const uniqueSuggestions = suggestions.filter((suggestion) => {
          const suggestionText =
            suggestion.formatted_address || suggestion.display_name;
          return suggestionText.toLowerCase() !== trimmedAddress.toLowerCase();
        });

        setEndSuggestions(uniqueSuggestions);
      } else {
        setEndSuggestions([]);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [endAddress, endSuggestionsCooldown]);

  // GPS Position ermitteln
  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation wird nicht unterstützt"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };
          setCurrentPosition(pos);
          resolve(pos);
        },
        (error) => {
          reject(
            new Error(
              "GPS-Position konnte nicht ermittelt werden: " + error.message
            )
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });
  };

  // GPS Position überwachen
  const watchPosition = () => {
    if (!navigator.geolocation) return null;

    return navigator.geolocation.watchPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setCurrentPosition(pos);

        // Prüfe ob nächste Anweisung erreicht wurde
        if (isNavigating && route && route.instructions) {
          checkInstructionProgress(pos);
        }
      },
      (error) => {
        console.error("GPS tracking error:", error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 10000,
      }
    );
  };

  // Prüfe Fortschritt der Navigation
  const checkInstructionProgress = (position) => {
    if (!route || !route.instructions) return;

    // Simuliere Fortschritt basierend auf Zeit (für Demo)
    // In echter App: Berechne Distanz zur nächsten Anweisung
    const now = Date.now();
    const progressInterval = 15000; // 15 Sekunden pro Anweisung (Demo)

    const expectedIndex = Math.floor(
      (now - (window.navigationStartTime || now)) / progressInterval
    );
    const newIndex = Math.min(expectedIndex, route.instructions.length - 1);

    if (
      newIndex > currentInstructionIndex &&
      newIndex < route.instructions.length
    ) {
      setCurrentInstructionIndex(newIndex);

      // Sprich neue Anweisung aus
      if (newIndex !== lastSpokenInstruction) {
        const instruction = route.instructions[newIndex];
        speakInstruction(instruction.text);
        setLastSpokenInstruction(newIndex);
      }
    }
  };

  // Navigation starten
  const startNavigation = async () => {
    try {
      await getCurrentPosition();
      setIsNavigating(true);
      setCurrentInstructionIndex(0);
      setLastSpokenInstruction(-1);
      window.navigationStartTime = Date.now();

      const watchId = watchPosition();

      // Speichere watch ID für cleanup
      if (watchId) {
        window.navigationWatchId = watchId;
      }

      // Sprich erste Anweisung
      if (route && route.instructions && route.instructions[0]) {
        setTimeout(() => {
          speakInstruction(
            "Navigation gestartet. " + route.instructions[0].text
          );
          setLastSpokenInstruction(0);
        }, 1000);
      }

      alert("🧭 Live-Navigation gestartet! GPS-Tracking aktiv.");
    } catch (error) {
      setError("GPS-Fehler: " + error.message);
    }
  };

  // Navigation stoppen
  const stopNavigation = () => {
    setIsNavigating(false);
    if (window.navigationWatchId) {
      navigator.geolocation.clearWatch(window.navigationWatchId);
      delete window.navigationWatchId;
    }
  };

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (window.navigationWatchId) {
        navigator.geolocation.clearWatch(window.navigationWatchId);
      }
    };
  }, []);

  // Aktuelle Position als Start setzen
  const useCurrentLocation = async () => {
    try {
      const position = await getCurrentPosition();
      // Reverse Geocoding für Adresse
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${position.lat}&lon=${position.lng}&format=json`
      );
      const data = await response.json();
      setStartAddress(data.display_name || `${position.lat}, ${position.lng}`);
    } catch (error) {
      setError(
        "Aktuelle Position konnte nicht ermittelt werden: " + error.message
      );
    }
  };

  const handleRouteCalculation = async () => {
    if (!startAddress || !endAddress) {
      setError("Bitte geben Sie Start- und Zieladresse ein");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(config.getApiUrl("/routing"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start: startAddress,
          end: endAddress,
          vehicle: "car",
          include_traffic: true, // Enable traffic-aware routing
        }),
      });

      const data = await response.json();
      console.log("Traffic-aware routing response:", data);

      if (data.success) {
        setRoute(data); // Store complete response with all routes
        setShowInstructions(true);
      } else {
        setError(data.error || "Fehler bei der Routenberechnung");
      }
    } catch (error) {
      console.error("Routing error:", error);
      setError("Verbindungsfehler beim Berechnen der Route");
    } finally {
      setLoading(false);
    }
  };

  const handleSwapAddresses = () => {
    const temp = startAddress;
    setStartAddress(endAddress);
    setEndAddress(temp);
  };

  const formatDuration = (seconds) => {
    // Robuste Behandlung von verschiedenen Eingabeformaten
    let totalSeconds = 0;

    if (typeof seconds === "number" && !isNaN(seconds)) {
      totalSeconds = seconds;
    } else if (typeof seconds === "string") {
      totalSeconds = parseFloat(seconds) || 0;
    } else {
      console.warn("Invalid duration format:", seconds);
      return "0 min";
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else if (minutes > 0) {
      return `${minutes}min`;
    } else {
      return "< 1min";
    }
  };

  const formatDistance = (meters) => {
    // Robuste Behandlung von verschiedenen Eingabeformaten
    let totalMeters = 0;

    if (typeof meters === "number" && !isNaN(meters)) {
      totalMeters = meters;
    } else if (typeof meters === "string") {
      totalMeters = parseFloat(meters) || 0;
    } else {
      console.warn("Invalid distance format:", meters);
      return "0 m";
    }

    if (totalMeters >= 1000) {
      return `${(totalMeters / 1000).toFixed(1)} km`;
    } else {
      return `${Math.round(totalMeters)} m`;
    }
  };

  // Handler für Route-Wechsel
  const handleRouteChange = (routeType) => {
    setSelectedRouteType(routeType);
  };

  // Aktuell ausgewählte Route bestimmen
  const getCurrentRoute = () => {
    if (!route) return null;

    const currentRouteType =
      selectedRouteType || route.recommended_route || "fastest";
    return {
      ...route,
      route: route.routes?.[currentRouteType] || route.route,
      current_route_type: currentRouteType,
    };
  };

  const currentRouteData = getCurrentRoute();

  const sidebarContent = (
    <Box sx={{ p: isMobile ? 1 : 2, height: "100%", overflow: "auto" }}>
      <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
        Routenplaner
      </Typography>

      {config.isDevelopment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Demo-Modus: Verwendet Mock-Daten
        </Alert>
      )}

      {/* Start Adresse */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Autocomplete
            freeSolo
            fullWidth
            clearOnBlur={false}
            selectOnFocus={false}
            options={startSuggestions}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option.formatted_address || option.display_name
            }
            inputValue={startAddress}
            onInputChange={(event, newInputValue) => {
              setStartAddress(newInputValue);
            }}
            open={startSuggestions.length > 0 && startAddress.length >= 2}
            ListboxProps={{
              style: {
                maxHeight: 300,
                zIndex: 9999,
                backgroundColor: "white",
              },
            }}
            PopperProps={{
              style: {
                zIndex: 9999,
              },
              placement: "bottom-start",
            }}
            onChange={(event, newValue) => {
              if (newValue && typeof newValue === "object") {
                const formatted =
                  newValue.formatted_address || newValue.display_name;

                // Prüfe ob es eine Straße/Adresse ist - dann füge Leerzeichen hinzu
                const isStreetOrAddress =
                  newValue.address &&
                  (newValue.address.road ||
                    newValue.type === "highway" ||
                    newValue.type === "residential" ||
                    newValue.type === "street");

                const finalAddress = isStreetOrAddress
                  ? formatted + " "
                  : formatted;
                setStartAddress(finalAddress);

                // Schließe die Dropdown-Liste und aktiviere Cooldown
                setStartSuggestions([]);
                setStartSuggestionsCooldown(true);

                // Cooldown nach 2 Sekunden beenden
                setTimeout(() => {
                  setStartSuggestionsCooldown(false);
                }, 2000);

                // Kleine Verzögerung, dann Cursor ans Ende setzen für weitere Eingabe
                setTimeout(() => {
                  const input = event.target
                    .closest(".MuiAutocomplete-root")
                    ?.querySelector("input");
                  if (input) {
                    input.focus();
                    input.setSelectionRange(
                      finalAddress.length,
                      finalAddress.length
                    );
                  }
                }, 50);
              }
            }}
            onClose={() => {
              // Behalte den Fokus für weitere Eingaben
              setTimeout(() => {
                const input = document.querySelector(
                  'input[placeholder*="Startadresse"], input[aria-label*="Startadresse"]'
                );
                if (input && document.activeElement !== input) {
                  input.focus();
                }
              }, 100);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Startadresse"
                variant="outlined"
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2">
                    {option.formatted_address || option.display_name}
                  </Typography>
                  {option.address && (
                    <Typography variant="caption" color="text.secondary">
                      {option.address.postcode && `${option.address.postcode} `}
                      {option.address.state || option.address.country}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          />
          <Button
            variant="outlined"
            onClick={useCurrentLocation}
            sx={{ minWidth: isMobile ? "100%" : "120px" }}
            size="small"
          >
            📍 Aktuell
          </Button>
        </Box>
      </Box>

      {/* Swap Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <IconButton onClick={handleSwapAddresses}>
          <SwapIcon />
        </IconButton>
      </Box>

      {/* Ziel Adresse */}
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          freeSolo
          clearOnBlur={false}
          selectOnFocus={false}
          options={endSuggestions}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : option.formatted_address || option.display_name
          }
          inputValue={endAddress}
          onInputChange={(event, newInputValue) => {
            setEndAddress(newInputValue);
          }}
          open={endSuggestions.length > 0 && endAddress.length >= 2}
          ListboxProps={{
            style: {
              maxHeight: 300,
              zIndex: 9999,
              backgroundColor: "white",
            },
          }}
          PopperProps={{
            style: {
              zIndex: 9999,
            },
            placement: "bottom-start",
          }}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue === "object") {
              const formatted =
                newValue.formatted_address || newValue.display_name;

              // Prüfe ob es eine Straße/Adresse ist - dann füge Leerzeichen hinzu
              const isStreetOrAddress =
                newValue.address &&
                (newValue.address.road ||
                  newValue.type === "highway" ||
                  newValue.type === "residential" ||
                  newValue.type === "street");

              const finalAddress = isStreetOrAddress
                ? formatted + " "
                : formatted;
              setEndAddress(finalAddress);

              // Schließe die Dropdown-Liste und aktiviere Cooldown
              setEndSuggestions([]);
              setEndSuggestionsCooldown(true);

              // Cooldown nach 2 Sekunden beenden
              setTimeout(() => {
                setEndSuggestionsCooldown(false);
              }, 2000);

              // Kleine Verzögerung, dann Cursor ans Ende setzen für weitere Eingabe
              setTimeout(() => {
                const input = event.target
                  .closest(".MuiAutocomplete-root")
                  ?.querySelector("input");
                if (input) {
                  input.focus();
                  input.setSelectionRange(
                    finalAddress.length,
                    finalAddress.length
                  );
                }
              }, 50);
            }
          }}
          onClose={() => {
            // Behalte den Fokus für weitere Eingaben
            setTimeout(() => {
              const input = document.querySelector(
                'input[placeholder*="Zieladresse"], input[aria-label*="Zieladresse"]'
              );
              if (input && document.activeElement !== input) {
                input.focus();
              }
            }, 100);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Zieladresse"
              variant="outlined"
              size="small"
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body2">
                  {option.formatted_address || option.display_name}
                </Typography>
                {option.address && (
                  <Typography variant="caption" color="text.secondary">
                    {option.address.postcode && `${option.address.postcode} `}
                    {option.address.state || option.address.country}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        />
      </Box>

      {/* Route berechnen Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleRouteCalculation}
        disabled={loading || !startAddress || !endAddress}
        startIcon={
          loading ? <CircularProgress size={20} /> : <NavigationIcon />
        }
        sx={{ mb: 2 }}
      >
        {loading ? "Berechne Route..." : "Route berechnen"}
      </Button>

      {/* Fehler anzeigen */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Route Informationen mit Traffic-Awareness */}
      {route && (
        <>
          {/* Traffic Information Summary */}
          {route.traffic_info && route.traffic_info.has_traffic_data && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                🚦 Live-Verkehrsdaten berücksichtigt:{" "}
                {route.traffic_info.traffic_segments} Verkehrssegmente,{" "}
                {route.traffic_info.incidents_count} Störungen
              </Typography>
              {route.traffic_info.recommended_route !== "fastest" && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                  ⚠️ Alternative Route empfohlen wegen Verkehrslage!
                </Typography>
              )}
            </Alert>
          )}

          {/* Recommended Route Card */}
          <Card
            sx={{
              mb: 2,
              border: route.traffic_info?.recommended_route
                ? "2px solid"
                : "none",
              borderColor: "success.main",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  {(selectedRouteType || route.recommended_route) === "fastest"
                    ? "🚗 Schnellste Route"
                    : (selectedRouteType || route.recommended_route) ===
                      "no_autobahn"
                    ? "🛣️ Ohne Autobahn"
                    : (selectedRouteType || route.recommended_route) ===
                      "shortest"
                    ? "📏 Kürzeste Route"
                    : "Aktuelle Route"}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {(selectedRouteType || route.recommended_route) ===
                    route.recommended_route && (
                    <Chip label="Empfohlen" color="success" size="small" />
                  )}
                  {selectedRouteType &&
                    selectedRouteType !== route.recommended_route && (
                      <Chip label="Alternative" color="primary" size="small" />
                    )}
                </Box>
              </Box>

              {/* Time and Distance Info */}
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {currentRouteData?.route?.traffic_adjustment ? (
                  <>
                    <Chip
                      icon={<TimeIcon />}
                      label={formatDuration(
                        currentRouteData.route.traffic_adjustment.adjusted_time
                      )}
                      color="primary"
                      size="small"
                    />
                    {currentRouteData.route.traffic_adjustment.original_time !==
                      currentRouteData.route.traffic_adjustment
                        .adjusted_time && (
                      <Chip
                        label={`+${formatDuration(
                          currentRouteData.route.traffic_adjustment
                            .adjusted_time -
                            currentRouteData.route.traffic_adjustment
                              .original_time
                        )} Verzögerung`}
                        color="warning"
                        size="small"
                      />
                    )}
                  </>
                ) : (
                  <Chip
                    icon={<TimeIcon />}
                    label={formatDuration(
                      currentRouteData?.route?.duration ||
                        route.route?.duration ||
                        0
                    )}
                    color="primary"
                    size="small"
                  />
                )}
                <Chip
                  icon={<DistanceIcon />}
                  label={formatDistance(
                    currentRouteData?.route?.distance ||
                      route.route?.distance ||
                      0
                  )}
                  color="secondary"
                  size="small"
                />
              </Box>

              {/* Traffic Delays Detail */}
              {currentRouteData?.route?.traffic_adjustment?.traffic_delays &&
                currentRouteData.route.traffic_adjustment.traffic_delays
                  .length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Verkehrsstörungen auf der Route:
                    </Typography>
                    {currentRouteData.route.traffic_adjustment.traffic_delays
                      .slice(0, 3)
                      .map((delay, index) => (
                        <Typography
                          key={index}
                          variant="caption"
                          sx={{ display: "block" }}
                        >
                          • {delay.location}:{" "}
                          {delay.severity === "heavy"
                            ? "Starker Verkehr"
                            : delay.severity === "moderate"
                            ? "Mäßiger Verkehr"
                            : delay.severity.startsWith("incident_")
                            ? "Störung"
                            : delay.severity}
                          (+{formatDuration(delay.delay_seconds)})
                        </Typography>
                      ))}
                    {currentRouteData.route.traffic_adjustment.traffic_delays
                      .length > 3 && (
                      <Typography variant="caption">
                        ... und{" "}
                        {currentRouteData.route.traffic_adjustment
                          .traffic_delays.length - 3}{" "}
                        weitere
                      </Typography>
                    )}
                  </Alert>
                )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: 2,
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                {!isNavigating ? (
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    onClick={startNavigation}
                  >
                    🧭 Navigation starten
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={stopNavigation}
                  >
                    ⏹️ Navigation stoppen
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    stopNavigation();
                    setRoute(null);
                    setShowInstructions(false);
                  }}
                  sx={{ minWidth: isMobile ? "100%" : "auto" }}
                >
                  Löschen
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Interactive Route Selection */}
          {route.routes && Object.keys(route.routes).length > 1 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Verfügbare Routen
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Klicken Sie auf eine Route um sie auszuwählen und auf der
                  Karte anzuzeigen:
                </Typography>

                {Object.entries(route.routes).map(([routeType, routeData]) => {
                  const isSelected =
                    (selectedRouteType || route.recommended_route) ===
                    routeType;
                  const isRecommended = routeType === route.recommended_route;

                  return (
                    <Card
                      key={routeType}
                      variant="outlined"
                      sx={{
                        mb: 1,
                        cursor: "pointer",
                        border: isSelected ? "2px solid" : "1px solid",
                        borderColor: isSelected ? "primary.main" : "divider",
                        backgroundColor: isSelected
                          ? "primary.light"
                          : "background.paper",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? "primary.light"
                            : "action.hover",
                        },
                      }}
                      onClick={() => handleRouteChange(routeType)}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                              >
                                {routeType === "fastest"
                                  ? "🚗 Schnellste Route"
                                  : routeType === "no_autobahn"
                                  ? "🛣️ Ohne Autobahn"
                                  : routeType === "shortest"
                                  ? "📏 Kürzeste Route"
                                  : routeType}
                              </Typography>
                              {isRecommended && (
                                <Chip
                                  label="Empfohlen"
                                  color="success"
                                  size="small"
                                />
                              )}
                              {isSelected && (
                                <Chip
                                  label="Ausgewählt"
                                  color="primary"
                                  size="small"
                                />
                              )}
                            </Box>

                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              <Chip
                                label={formatDuration(
                                  routeData.traffic_adjustment?.adjusted_time ||
                                    routeData.duration
                                )}
                                size="small"
                                color={isSelected ? "primary" : "default"}
                                icon={<TimeIcon />}
                              />
                              <Chip
                                label={formatDistance(routeData.distance)}
                                size="small"
                                color={isSelected ? "primary" : "default"}
                                icon={<DistanceIcon />}
                              />
                            </Box>

                            {routeData.traffic_adjustment?.traffic_delays &&
                              routeData.traffic_adjustment.traffic_delays
                                .length > 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  ⚠️{" "}
                                  {
                                    routeData.traffic_adjustment.traffic_delays
                                      .length
                                  }{" "}
                                  Verkehrsstörung(en)
                                  {routeData.traffic_adjustment.traffic_delays
                                    .length > 0 && (
                                    <span style={{ marginLeft: 8 }}>
                                      (+
                                      {formatDuration(
                                        routeData.traffic_adjustment.traffic_delays.reduce(
                                          (sum, delay) =>
                                            sum + delay.delay_seconds,
                                          0
                                        )
                                      )}{" "}
                                      Verzögerung)
                                    </span>
                                  )}
                                </Typography>
                              )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  💡 Tipp: Auf der Karte werden alle Routen angezeigt - die
                  ausgewählte Route in Blau, andere gestrichelt
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Live Navigation */}
      {isNavigating &&
        route &&
        (route.route?.instructions || route.instructions) && (
          <Card sx={{ mb: 2, bgcolor: "success.light" }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ color: "success.contrastText", mb: 2 }}
              >
                🧭 Live Navigation
              </Typography>

              {currentPosition && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  GPS: {currentPosition.lat.toFixed(6)},{" "}
                  {currentPosition.lng.toFixed(6)}
                  <br />
                  Genauigkeit: {currentPosition.accuracy}m
                </Alert>
              )}

              <Card sx={{ mb: 2, bgcolor: "primary.main" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ color: "primary.contrastText" }}
                  >
                    Nächste Anweisung:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "primary.contrastText", mt: 1 }}
                  >
                    {(route.route?.instructions || route.instructions)?.[
                      currentInstructionIndex
                    ]?.text || "Ziel erreicht!"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "primary.contrastText" }}
                  >
                    {formatDistance(
                      (route.route?.instructions || route.instructions)?.[
                        currentInstructionIndex
                      ]?.distance || 0
                    )}
                  </Typography>
                </CardContent>
              </Card>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "success.contrastText" }}
                >
                  Schritt {currentInstructionIndex + 1} von{" "}
                  {(route.route?.instructions || route.instructions)?.length ||
                    0}
                </Typography>

                <IconButton
                  onClick={() => setVoiceEnabled(!voiceEnabled)}
                  sx={{ color: "success.contrastText" }}
                  size="small"
                >
                  {voiceEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        )}

      {/* Fahranweisungen */}
      {route &&
        (route.route?.instructions || route.instructions) &&
        showInstructions &&
        !isNavigating && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Fahranweisungen</Typography>
                <Button
                  size="small"
                  onClick={() => setShowInstructions(!showInstructions)}
                >
                  {showInstructions ? "Ausblenden" : "Anzeigen"}
                </Button>
              </Box>

              <List dense>
                {(route.route?.instructions || route.instructions || []).map(
                  (instruction, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={instruction.text}
                          secondary={`${formatDistance(
                            instruction.distance || 0
                          )}`}
                        />
                      </ListItem>
                      {index <
                        (route.route?.instructions || route.instructions || [])
                          .length -
                          1 && <Divider />}
                    </React.Fragment>
                  )
                )}
              </List>
            </CardContent>
          </Card>
        )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      {isMobile ? (
        <>
          {/* Mobile: Karte oben */}
          <Box
            sx={{ height: isNavigating ? "60%" : "50%", position: "relative" }}
          >
            <MapView
              route={currentRouteData?.route || route?.route || route}
              routes={route?.routes}
              recommendedRoute={route?.recommended_route}
              selectedRoute={selectedRouteType || route?.recommended_route}
              center={
                currentPosition
                  ? [currentPosition.lat, currentPosition.lng]
                  : [51.1657, 10.4515]
              }
              zoom={currentPosition ? 15 : 6}
              currentPosition={currentPosition}
              isNavigating={isNavigating}
            />
            {/* Mobile Sidebar Toggle */}
            {!isNavigating && (
              <Button
                variant="contained"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                sx={{
                  position: "absolute",
                  top: 16,
                  left: 16,
                  zIndex: 1000,
                  minWidth: "auto",
                  px: 2,
                }}
              >
                {sidebarOpen ? "Karte" : "Menü"}
              </Button>
            )}
          </Box>

          {/* Mobile: Sidebar als Drawer */}
          <Drawer
            variant="persistent"
            anchor="bottom"
            open={sidebarOpen}
            sx={{
              "& .MuiDrawer-paper": {
                height: isNavigating ? "40%" : "50%",
                overflow: "hidden",
              },
            }}
          >
            {sidebarContent}
          </Drawer>
        </>
      ) : (
        <>
          {/* Desktop: Sidebar links */}
          <Paper sx={{ width: 400, overflow: "hidden" }}>
            {sidebarContent}
          </Paper>

          {/* Desktop: Karte rechts */}
          <Box sx={{ flex: 1 }}>
            <MapView
              route={currentRouteData?.route || route?.route || route}
              routes={route?.routes}
              recommendedRoute={route?.recommended_route}
              selectedRoute={selectedRouteType || route?.recommended_route}
              center={
                currentPosition
                  ? [currentPosition.lat, currentPosition.lng]
                  : [51.1657, 10.4515]
              }
              zoom={currentPosition ? 15 : 6}
              currentPosition={currentPosition}
              isNavigating={isNavigating}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default RoutePlanner;
